import React from "react";
import ReactEcharts from "echarts-for-react";

// Function to calculate the linear regression (y = mx + b)
const getLinearTrend = (data) => {
  const n = data.length;
  const x = Array.from({ length: n }, (_, i) => i + 1); // Index as x-axis values
  const y = data;

  // Calculate the sum of x, y, x^2, and xy
  const sumX = x.reduce((acc, val) => acc + val, 0);
  const sumY = y.reduce((acc, val) => acc + val, 0);
  const sumXY = x.reduce((acc, val, idx) => acc + val * y[idx], 0);
  const sumX2 = x.reduce((acc, val) => acc + val * val, 0);

  // Calculate the slope (m) and intercept (b)
  const m = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const b = (sumY - m * sumX) / n;

  // Calculate the trend line values (y = mx + b) for each x
  const trendLine = x.map((val) => m * val + b);
  return { m, b, trendLine };
};

// Function to forecast values based on the trend line equation
const forecastValues = (m, b, startIndex, endIndex) => {
  const forecast = [];
  for (let i = startIndex; i <= endIndex; i++) {
    forecast.push(m * i + b);
  }
  return forecast;
};

// Function to format date as "Mon-yy"
const formatDate = (date) => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of the year
  return `${month}-${year}`;
};

const RevnewProfit = () => {
  const dates = [
    "Nov-19", "Dec-19", "Jan-20", "Feb-20", "Mar-20", "Apr-20", "May-20", "Jun-20", "Jul-20", "Aug-20", "Sep-20", "Oct-20", 
    "Nov-20", "Dec-20", "Jan-21", "Feb-21", "Mar-21", "Apr-21", "May-21", "Jun-21", "Jul-21", "Aug-21", "Sep-21", "Oct-21", 
    "Nov-21", "Dec-21", "Jan-22", "Feb-22", "Mar-22", "Apr-22", "May-22", "Jun-22", "Jul-22", "Aug-22", "Sep-22", "Oct-22", 
    "Nov-22", "Dec-22", "Jan-23", "Feb-23", "Mar-23", "Apr-23", "May-23", "Jun-23", "Jul-23", "Aug-23", "Sep-23", "Oct-23", 
    "Nov-23", "Dec-23", "Jan-24", "Feb-24", "Mar-24", "Apr-24", "May-24", "Jun-24", "Jul-24", "Aug-24", "Sep-24", "Oct-24",
    "Nov-24", "Dec-24", "Jan-25", "Feb-25", "Mar-25", "Apr-25", "May-25", "Jun-25", "Jul-25", "Aug-25", "Sep-25"
  ];

  const normalisedEBIT = [
    28226.72, 22186.79, 13886.79, 25351.96, 15094.72, 45016.06, 31062.23, 30724.37, 6462.78, 8652.49, 23546.51, -1504.00, 
    13166.06, -7774.80, 13374.33, 17579.41, 18138.20, -5949.39, 28300.35, -2240.74, 18694.59, -3045.65, 10312.21, 7476.94, 
    12819.05, 13675.66, 11036.13, -13093.15, 16938.17, -15102.22, 14223.22, -1479.37, -31507.13, 6710.67, 6924.14, 23664.22, 
    -1594.82, 6200.17, -4657.15, 4416.32, 5579.37, 2653.62, -3236.28, 1321.46, 4362.70, 38502.93, 14401.22, 32591.80, 12352.53, 
    14380.50, 797.54, 2991.23, 376.79, 2383.10, 10066.50, 33540.38, 1546.90, 19097.25, 31175.08, 31233.53
  ];

  const ebit = [
    27833.53, 22120.10, 13866.79, 25291.14, 14750.19, 42249.96, 30901.47, 30687.92, 5507.86, 6366.99, 21428.33, -1504.00, 
    35685.22, -1274.80, 13374.33, 14230.13, 17306.71, -6353.44, 27972.55, -2762.75, 23751.62, -3736.48, 10312.21, 7276.94, 
    11819.05, 12514.75, 8901.64, -13184.06, 16662.17, -16251.92, 13754.22, -2058.03, -33078.33, 6462.05, 5891.56, 22469.25, 
    -2330.66, 5524.48, -5929.32, 3834.87, 4467.61, 2276.68, -4367.10, 1321.46, 4362.70, 36514.75, -3190.94, 31962.57, 12302.40, 
    14380.50, 196.04, 1938.60, -625.73, 1982.09, 9610.82, 33540.38, 1546.90, 19097.25, 31175.08, 31233.53
  ];

  const totalBusinessRevenue = [
    50000, 48000, 51000, 47000, 49000, 53000, 52000, 51000, 50000, 49000, 50500, 49500, 
    47000, 46000, 49000, 49500, 50500, 47000, 52000, 51000, 50000, 53000, 54000, 55000, 
    51000, 50500, 52000, 53000, 54000, 55000, 53000, 52000, 54000, 50500, 51000, 49000,
    48000, 50000, 53000, 54000, 51000, 50000, 51000, 52000, 50500, 49000, 49500, 51000,
    53000, 54000, 55000, 52000, 51000, 50500, 49000, 49500, 51000, 53000, 52000, 54000
  ];

  // Calculate trend lines for EBIT and Total Business Revenue
  const { m: mEBIT, b: bEBIT, trendLine: trendLineEBIT } = getLinearTrend(ebit);
  const { m: mRevenue, b: bRevenue, trendLine: trendLineRevenue } = getLinearTrend(totalBusinessRevenue);

  // Forecast future values (from Apr-24 to Sept-25)
  const forecastStartIndex = dates.length + 1; // next index after the last date
  const forecastEndIndex = forecastStartIndex + (6 + 12); // forecast till Sep-25

  const forecastEBIT = forecastValues(mEBIT, bEBIT, forecastStartIndex, forecastEndIndex);
  const forecastRevenue = forecastValues(mRevenue, bRevenue, forecastStartIndex, forecastEndIndex);

  // Extend the dates for the forecast period
  const forecastDates = [
    ...dates,
    ...Array.from({ length: forecastEndIndex - dates.length }, (_, i) => {
      const lastMonth = new Date(`01-${dates[dates.length - 1]}`);
      lastMonth.setMonth(lastMonth.getMonth() + i + 1);
      return formatDate(lastMonth);
    }),
  ];

  // Add the forecasted data points to the series
  const option = {
    tooltip: { trigger: "axis" },
    legend: { data: ["Total Business Revenue", "EBIT", "Normalised EBIT", "Trend Line (EBIT)", "Trend Line (Revenue)"] },
    xAxis: { type: "category", data: forecastDates },
    yAxis: { type: "value" },
    series: [
      {
        name: "Total Business Revenue",
        type: "line",
        data: [...totalBusinessRevenue, ...forecastRevenue],
        smooth: true,
      },
      {
        name: "EBIT",
        type: "line",
        data: [...ebit, ...forecastEBIT],
        smooth: true,
      },
      {
        name: "Normalised EBIT",
        type: "line",
        data: normalisedEBIT,
        smooth: true,
      },
      {
        name: "Trend Line (Revenue)",
        type: "line",
        data: [...trendLineRevenue, ...forecastRevenue],
        smooth: true,
        lineStyle: { type: "dashed", color: "blue" },
      },
      {
        name: "Trend Line (EBIT)",
        type: "line",
        data: [...trendLineEBIT, ...forecastEBIT],
        smooth: true,
        lineStyle: { type: "dashed", color: "green" },
      },
    ],
  };

  return <ReactEcharts option={option} style={{ height: "500px" }} />;
};

export default RevnewProfit;
